import { useRef } from "react";

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function RadioGroup({ options, value, onChange, fieldId }) {
  return (
    <div className="radio-group">
      {options.map((opt) => {
        const checked = value === opt;
        return (
          <label key={opt} className={`radio-option${checked ? " checked" : ""}`}>
            <input
              type="radio"
              name={fieldId}
              value={opt}
              checked={checked}
              onChange={() => onChange(opt)}
            />
            <span className="radio-dot" />
            {opt}
          </label>
        );
      })}
    </div>
  );
}

function FileUpload({ questionId, files, onChange }) {
  const inputRef = useRef();

  const handleFiles = (incoming) => {
    const arr = Array.from(incoming);
    onChange(questionId, (prev) => [...(prev || []), ...arr]);
  };

  const removeFile = (index) => {
    onChange(questionId, (prev) => prev.filter((_, i) => i !== index));
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      <div
        className="file-upload-area"
        onClick={() => inputRef.current.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className="file-upload-icon">📎</div>
        <div className="file-upload-text">
          Drag files here or <span>browse</span>
        </div>
      </div>

      {files && files.length > 0 && (
        <div className="file-list">
          {files.map((file, i) => (
            <div key={i} className="file-chip">
              <span className="file-chip-name">{file.name}</span>
              <span className="file-chip-size">{formatBytes(file.size)}</span>
              <button className="file-chip-remove" onClick={() => removeFile(i)}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldControl({ field, value, onChange, fileMap, onFileChange, errors }) {
  const hasError = errors && errors[field.id];

  switch (field.type) {
    case "short_text":
      return (
        <input
          type="text"
          className={hasError ? "error" : ""}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      );

    case "short_text_limited":
      return (
        <>
          <input
            type="text"
            className={hasError ? "error" : ""}
            maxLength={field.maxLength}
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
          />
          <div className="char-hint">{(value || "").length}/{field.maxLength}</div>
        </>
      );

    case "long_text":
      return (
        <textarea
          className={hasError ? "error" : ""}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
          rows={5}
        />
      );

    case "integer":
      return (
        <input
          type="number"
          className={hasError ? "error" : ""}
          min="0"
          step="1"
          value={value === undefined || value === null || value === "" ? "" : value}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(field.id, raw === "" ? "" : parseInt(raw, 10));
          }}
        />
      );

    case "date":
      return (
        <input
          type="date"
          className={hasError ? "error" : ""}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      );

    case "time":
      return (
        <input
          type="time"
          className={hasError ? "error" : ""}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
        />
      );

    case "yn":
      return (
        <RadioGroup
          options={["Yes", "No"]}
          value={value || ""}
          onChange={(v) => onChange(field.id, v)}
          fieldId={field.id}
        />
      );

    case "selection":
      if (field.options.length <= 3) {
        return (
          <RadioGroup
            options={field.options}
            value={value || ""}
            onChange={(v) => onChange(field.id, v)}
            fieldId={field.id}
          />
        );
      }
      return (
        <select
          className={hasError ? "error" : ""}
          value={value || ""}
          onChange={(e) => onChange(field.id, e.target.value)}
        >
          <option value="">— Select —</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    case "attachment":
      return (
        <FileUpload
          questionId={field.id}
          files={fileMap[field.id] || []}
          onChange={onFileChange}
        />
      );

    default:
      return null;
  }
}

export default function DynamicForm({ fields, answers, onAnswerChange, fileMap, onFileChange, errors }) {
  return (
    <>
      {fields.map((field) => (
        <div className="form-group" key={field.id}>
          <label>
            {field.num}. {field.label}
          </label>
          <FieldControl
            field={field}
            value={answers[field.id]}
            onChange={onAnswerChange}
            fileMap={fileMap}
            onFileChange={onFileChange}
            errors={errors}
          />
          {errors && errors[field.id] && (
            <div className="form-error">{errors[field.id]}</div>
          )}
        </div>
      ))}
    </>
  );
}
